import React, { useEffect } from "react"
import { Box } from "@mui/material"
import { useWeb3React } from "@web3-react/core"
import _ from "lodash"
import { useTranslation } from "react-i18next"
import { BIG_ZERO } from "src/constants/bigNumber"
import { AggregatedBoostData } from "../../types"
import VaultInnerCard from "./VaultInnerCard"
import Button from "src/components/Button"
import { useUserFerroBoost } from "src/state/xFer/hooks"
import { useVaultsApr } from "../../hooks/useVaultsApr"
import { useAction } from "src/hooks/useAction"
import { actions, fetchFerroBoostUserDataAsync } from "src/state/xFer"
import useFerroBoostHandlers from "../../hooks/useFerroBoostHandlers"
import { useDispatch } from "react-redux"
import Web3Status from "src/components/Web3Status"
import Text from "src/components/Text"
import { enqueueToast } from "src/components/Toastify"

interface Props {
  defaultOpenedPid: string | null
}

const MyVault: React.FC<Props> = ({ defaultOpenedPid }: Props) => {
  const { t } = useTranslation()
  const { account } = useWeb3React()

  const dispatch = useDispatch()
  const { boostData } = useUserFerroBoost()
  const vaultsApr = useVaultsApr()
  const earnMaxApr =
    vaultsApr.length === 0
      ? 0
      : Math.max(...vaultsApr.map((x) => parseFloat(x.apr)))
  const { allowance } = boostData

  const { onApprove } = useFerroBoostHandlers()

  const handleApprove = async () => {
    try {
      await onApprove()
      if (account) {
        dispatch(fetchFerroBoostUserDataAsync(account))
      }
    } catch (e) {
      console.error(e)
      enqueueToast("error", t("xFER is not approved"), t("Transaction Failed"))
    }
  }

  const openDepositModal = useAction(actions.setOpenedDepositModal, {
    defaultSelectedPool: null,
  })

  useEffect(() => {
    if (defaultOpenedPid !== null) {
      dispatch(
        actions.setOpenedDepositModal({
          defaultSelectedPool: parseInt(defaultOpenedPid),
        }),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const renderBody = () => {
    if (!account) {
      return (
        <Box textAlign="center">
          {t("Connect to a wallet to view your vaults.")}
        </Box>
      )
    }
    if (boostData.stakes.filter((x) => x.active).length === 0) {
      return (
        <Box bgcolor="#00000080" p={3} borderRadius="10px">
          <Text variant="h5">{t("No Vaults Found")}</Text>
          <Box mt={1}>
            {t("Deposit your xFER now and earn up to ")}
            {earnMaxApr}% APR
          </Box>
        </Box>
      )
    }

    const grouped = _.groupBy(
      boostData.stakes
        .map((x, i) => ({
          ...x,
          stakeId: i,
        }))
        .filter((x) => x.active),
      (p) => p.poolId,
    )

    const aggregated: AggregatedBoostData[] = Object.keys(grouped).map((k) =>
      grouped[k]
        .map((x) => ({
          ...x,
          stakeIds: [] as number[],
          withdrawableStakeIds: [] as number[],
        }))
        .reduce(
          (a, b) => {
            const { withdrawableStakeIds, stakeIds } = a
            const isPeriodFulfilled =
              b.unlockTimestamp <= new Date().getTime() / 1000
            stakeIds.push(b.stakeId)
            if (isPeriodFulfilled) withdrawableStakeIds.push(b.stakeId)
            return {
              ...a,
              amount: a.amount.add(b.amount),
              totalLocked: a.totalLocked.add(
                !isPeriodFulfilled ? b.amount : BIG_ZERO,
              ),
              totalUnlocked: a.totalUnlocked.add(
                isPeriodFulfilled ? b.amount : BIG_ZERO,
              ),
              poolId: b.poolId,
              stakeIds,
              withdrawableStakeIds,
              lastUnlockTimestamp: Math.max(
                a.lastUnlockTimestamp,
                b.unlockTimestamp,
              ),
            }
          },
          {
            amount: BIG_ZERO,
            totalLocked: BIG_ZERO,
            totalUnlocked: BIG_ZERO,
            poolId: 0,
            stakeTimestamp: 0,
            unlockTimestamp: 0,
            active: false,
            stakeIds: [],
            withdrawableStakeIds: [],
            lastUnlockTimestamp: 0,
          } as AggregatedBoostData,
        ),
    )

    return aggregated.map((stake) => (
      <VaultInnerCard
        {...stake}
        totalDeposited={stake.amount}
        key={stake.poolId}
        defaultOpened={defaultOpenedPid === stake.poolId.toString()}
      />
    ))
  }

  return (
    <Box>
      {renderBody()}
      <Box style={{ textAlign: "center" }} mt={3}>
        {!account ? (
          <Web3Status fullWidth />
        ) : allowance && allowance.gt("0") ? (
          <Button fullWidth onClick={openDepositModal}>
            <Box>{t("Deposit xFER")}</Box>
          </Button>
        ) : (
          <Button fullWidth onClick={handleApprove}>
            {t("Enable")}
          </Button>
        )}
      </Box>
    </Box>
  )
}

export default MyVault
